<script setup lang="ts">
const props = defineProps<{ id: string }>();
const { t } = useI18n({ useScope: "local" });
const { block, field, patch } = useInlineBlockEditor(props.id);

type SeriesRow = { key: string; label: string; color: string };

const height = field<number>("height", 400);
const chartConfig = field<Record<string, { label?: string; color?: string }>>("chartConfig", {});
const seriesRows = ref<SeriesRow[]>([]);

const normalizeSeriesRows = (config: Record<string, { label?: string; color?: string }> | undefined): SeriesRow[] =>
  Object.entries(config ?? {}).map(([key, value]) => ({
    key,
    label: value?.label ?? key,
    color: value?.color ?? "",
  }));

const toChartConfig = (rows: SeriesRow[]) =>
  rows.reduce<Record<string, { label: string; color: string }>>((config, row) => {
    const key = row.key.trim();
    if (key) {
      config[key] = { label: row.label || key, color: row.color };
    }
    return config;
  }, {});

watch(
  () => block.value?.chartConfig,
  (config) => {
    seriesRows.value = normalizeSeriesRows(config as Record<string, { label?: string; color?: string }> | undefined);
  },
  { immediate: true },
);

const addSeries = () => {
  seriesRows.value = [...seriesRows.value, { key: "", label: "", color: "" }];
};

const updateSeries = (index: number, value: Partial<SeriesRow>) => {
  seriesRows.value = seriesRows.value.map((row, rowIndex) => (rowIndex === index ? { ...row, ...value } : row));
  patch({ chartConfig: toChartConfig(seriesRows.value) });
};

const removeSeries = (index: number) => {
  seriesRows.value = seriesRows.value.filter((_, rowIndex) => rowIndex !== index);
  patch({ chartConfig: toChartConfig(seriesRows.value) });
};
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:chart-column-big" accent="reporting">
    <EditorField :label="t('heightLabel')">
      <Input v-model.number="height" type="number" min="0" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <div class="space-y-2">
      <div class="flex items-center justify-between gap-2">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{{ t("seriesLabel") }}</p>
        <Button variant="outline" size="sm" class="h-8 rounded-sm text-[13px]" @click="addSeries">
          <Icon mode="svg" size="15" name="lucide:plus" class="mr-1.5" />
          {{ t("addSeries") }}
        </Button>
      </div>

      <div v-for="(series, index) in seriesRows" :key="`${series.key}-${index}`" class="space-y-2 rounded-md border bg-card p-2">
        <Input :model-value="series.key" class="h-8 rounded-sm bg-muted text-[13px]" :placeholder="t('keyPlaceholder')" @update:model-value="(value) => updateSeries(index, { key: String(value) })" />
        <Input :model-value="series.label" class="h-8 rounded-sm bg-muted text-[13px]" :placeholder="t('labelPlaceholder')" @update:model-value="(value) => updateSeries(index, { label: String(value) })" />
        <div class="grid grid-cols-[1fr_auto] gap-2">
          <EditorThemeColourInput :model-value="series.color" output="css" placeholder="Theme colour or CSS value" @update:model-value="(value) => updateSeries(index, { color: value })" />
          <Button variant="ghost" size="icon" class="h-8 w-8 rounded-sm text-destructive" @click="removeSeries(index)">
            <Icon mode="svg" size="15" name="lucide:trash-2" />
          </Button>
        </div>
      </div>
    </div>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Single Chart Container"
  heightLabel: "Height"
  seriesLabel: "Series Configuration"
  addSeries: "Add Series"
  keyPlaceholder: "Series key"
  labelPlaceholder: "Label"
</i18n>
